import { Modal, OverflowTooltip, Table, UsePrivileges } from "@4uhub/lib4uhub";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ISyncPerson } from "../../../../models/sync-person";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import SyncPersonDetailsModal from "./SyncPersonDetailsModal";
import SyncPersonTransactionsModal from "./SyncPersonTransactionsModal";

const SYNC_PERSON_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/SyncPerson/SyncPersons";

const syncPersonService = new MainApi<ISyncPerson>(SYNC_PERSON_ROUTE);

const translationPath = "page.4uBus.logs.syncPersons.";

const FULL_DATA_DOMAIN_NAME = "4ubuslogssyncpersonfull";

const toApiDate = (date: Date) =>
  new Date(new Date(date).setHours(15)).toISOString().substring(0, 10);

const SyncPersons = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<ISyncPerson | null>(
    null,
  );
  const [detailsPerson, setDetailsPerson] = useState<ISyncPerson | null>(null);
  const [pageRows, setPageRows] = useState<ISyncPerson[]>([]);
  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Name: "",
    Cpf: "",
    TasyIdentifier: "",
    LastModifiedStart: null,
    LastModifiedEnd: null,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { privileges } = UsePrivileges();

  const canViewFullData = privileges.some(
    (privilege) =>
      privilege.domainName === "all" ||
      privilege.domainName === FULL_DATA_DOMAIN_NAME,
  );

  const formatDate = useCallback(
    (value?: string | null, dateOnly = false) => {
      if (!value) return "-";

      return format(new Date(value), dateOnly ? "P" : "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  // A coluna só ocupa espaço quando alguma linha da página tem o identificador.
  const showTasyIdentifier = pageRows.some((row) =>
    Boolean(
      (row as ISyncPerson & { tasyIdentifier?: string | null }).tasyIdentifier,
    ),
  );

  const columns = useMemo<GridColDef<ISyncPerson>[]>(
    () => [
      {
        field: "name",
        headerName: t(translationPath + "name") || "Nome",
        flex: 1.4,
        minWidth: 180,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <OverflowTooltip
            text={params.value || "-"}
            tooltip={params.value || "-"}
            textTransform="none"
            variant="body2"
          />
        ),
      },
      {
        field: "cpf",
        headerName: t(translationPath + "cpf") || "CPF",
        flex: 0.8,
        minWidth: 130,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value || "-",
      },
      {
        field: "tasyIdentifier",
        headerName:
          t(translationPath + "tasy_identifier") || "Identificador Tasy",
        flex: 1,
        minWidth: 160,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <OverflowTooltip
            text={params.value || "-"}
            tooltip={params.value || "-"}
            textTransform="none"
            variant="body2"
          />
        ),
      },
      {
        field: "birthDate",
        headerName: t(translationPath + "birth_date") || "Data de nascimento",
        flex: 0.9,
        minWidth: 130,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDate(params.value, true),
      },
      {
        field: "system",
        headerName: t(translationPath + "system") || "Sistema",
        flex: 0.7,
        minWidth: 100,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value || "-",
      },
      {
        field: "lastModified",
        headerName:
          t(translationPath + "last_modified") || "Última atualização",
        flex: 1,
        minWidth: 165,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDate(params.value),
      },
    ],
    [formatDate, t],
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (column) => showTasyIdentifier || column.field !== "tasyIdentifier",
      ),
    [columns, showTasyIdentifier],
  );

  const handleFilterChange = useCallback(
    (filters: IFilterParams | ((previous: IFilterParams) => IFilterParams)) => {
      setFilterParams(filters);
      setRefresh((previous) => previous + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedPerson(null);
  }, []);

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedPerson && (
          <SyncPersonTransactionsModal
            person={selectedPerson}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      {detailsPerson && (
        <SyncPersonDetailsModal
          person={detailsPerson}
          onClose={() => setDetailsPerson(null)}
        />
      )}
      <Table<ISyncPerson>
        title={t("components.menu.4ubus.logs.syncPersons")}
        columns={visibleColumns}
        service={syncPersonService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") ||
          "Pesquisar por nome ou CPF..."
        }
        onFetchData={setPageRows}
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedPerson(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_transactions") || "Ver transações",
          },
          canViewFullData
            ? {
                action: () => setDetailsPerson(params.row),
                icon: <BadgeOutlinedIcon fontSize="small" />,
                text: t(translationPath + "view_all_data") || "Ver todos os dados",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            Name: filterParams.Name || undefined,
            Cpf: filterParams.Cpf || undefined,
            TasyIdentifier: filterParams.TasyIdentifier || undefined,
            LastModifiedStart:
              filterParams.LastModifiedStart && filterParams.LastModifiedEnd
                ? toApiDate(filterParams.LastModifiedStart)
                : undefined,
            LastModifiedEnd:
              filterParams.LastModifiedStart && filterParams.LastModifiedEnd
                ? toApiDate(filterParams.LastModifiedEnd)
                : undefined,
          }),
          [filterParams, refresh],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default SyncPersons;
